import React, { useState } from "react";
import { useDropzone } from "react-dropzone";
import { UploadCloud, X, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";

export default function GalleryDropzone({ value = [], onChange, schoolId }) {
  const [uploading, setUploading] = useState(false);
  const [progressState, setProgressState] = useState({ current: 0, total: 0, percent: 0 });

  const { getRootProps, getInputProps } = useDropzone({
    accept: { "image/*": [".png", ".jpg", ".jpeg", ".webp"] },
    onDrop: async (acceptedFiles) => {
      if (!acceptedFiles || acceptedFiles.length === 0) return;
      setUploading(true);
      setProgressState({ current: 0, total: acceptedFiles.length, percent: 0 });
      const newUrls = [...value];
      let currentIndex = 0;
      for (const file of acceptedFiles) {
        currentIndex++;
        try {
          const fd = new FormData();
          fd.append("file", file);
          fd.append("media_type", "gallery");
          const r = await api.post(`/api/v1/admin/schools/${schoolId}/upload-media`, fd, {
            headers: { "Content-Type": "multipart/form-data" },
            onUploadProgress: (progressEvent) => {
              const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
              setProgressState({ current: currentIndex, total: acceptedFiles.length, percent: percentCompleted });
            }
          });
          if (r.data?.url) {
            newUrls.push(r.data.url);
          }
        } catch (err) {
          console.error(err);
          toast.error(`${file.name} yüklenirken hata oluştu`);
        }
      }
      onChange(newUrls);
      setUploading(false);
    },
  });

  const removeImage = (url) => {
    onChange(value.filter((u) => u !== url));
  };

  return (
    <div className="space-y-4">
      <div {...getRootProps()} className={`border-2 border-dashed border-border rounded-lg p-8 text-center cursor-pointer transition-colors ${uploading ? "opacity-50 pointer-events-none" : "hover:bg-muted/40"}`}>
        <input {...getInputProps()} />
        {uploading ? (
          <div className="flex flex-col items-center gap-4 w-full max-w-md mx-auto">
            <Loader2 className="h-8 w-8 text-muted-foreground animate-spin" />
            <div className="w-full space-y-1">
              <Progress value={((progressState.current - 1 + progressState.percent / 100) / progressState.total) * 100} className="h-2 w-full" />
              <div className="text-sm font-medium flex justify-between">
                <span>{progressState.current} / {progressState.total} Görsel</span>
                <span>% {progressState.percent}</span>
              </div>
            </div>
            <div className="text-xs text-muted-foreground">Yükleniyor, lütfen bekleyin...</div>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-2">
            <UploadCloud className="h-8 w-8 text-muted-foreground" />
            <div className="text-sm font-medium">Görselleri sürükleyip bırakın veya seçin</div>
            <div className="text-xs text-muted-foreground">Toplu seçim yapabilirsiniz. Yüklenen resimler otomatik optimize edilir.</div>
          </div>
        )}
      </div>

      {value.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {value.map((url, i) => (
            <div key={i} className="relative group rounded-lg overflow-hidden border border-border">
              <img src={url} alt="" className="w-full h-24 object-cover" />
              <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                <Button variant="destructive" size="icon" className="h-8 w-8 rounded-full" onClick={() => removeImage(url)}>
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
